import { Download } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import {
  expressDownloadFilesFromRequest,
  expressGetRequiredFiles,
  expressUploadFilesFromRequest,
} from '../../api/express/files';
import {
  downloadFilesFromRequest,
  getRequiredFiles,
  uploadFilesFromRequest,
} from '../../api/files';
import CenteredCircularProgress from '../../designSystem/CircularProgress';
import { formatDateToLocal } from '../../designSystem/Table/cellFormat';
import UploaderInLine from '../../designSystem/UploaderInLine';
import { showAlert } from '../../functions/utils';

interface UploadListProps {
  products: string[];
  requestType: string;
  canDownload?: boolean;
  canUpload?: boolean;
}

type Requirement = {
  file: string | null;
  name: string;
  status: boolean;
  updatedAt: string;
};

type RequirementItemProps = {
  key: number;
  name: string;
  status: boolean;
  fileName: string | null;
  updatedAt: string;
  setUp: () => Promise<void>;
  requestType: string;
  canDownload?: boolean;
  canUpload?: boolean;
};

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
    },
    tableCell: {
      padding: '4px !important',
      maxWidth: '200px',
      maxHeight: '10px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    selectFullWidth: {
      width: '100%',
    },
    selectSmall: {
      width: '200px',
    },
  }),
);

const firstCellStyle = {
  maxWidth: '150px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  padding: '4px 16px',
};

export const RequirementItem = (props: RequirementItemProps) => {
  const {
    name,
    status,
    setUp,
    requestType,
    fileName,
    updatedAt,
    canDownload,
    canUpload,
  } = props;
  const [file, setFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [miniLoading, setMiniLoading] = useState(false);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      showAlert(dispatch, 'error', 'No se ha seleccionado ningún archivo');
      return;
    }
    const uploadedFile = event.target.files[0];
    setUploadedFileName(uploadedFile.name);
    setFile(uploadedFile);
  };

  const handleClearFile = () => {
    setFile(null);
    setUploadedFileName('');
  };

  const handleDownload = async () => {
    if (!canDownload) return;
    try {
      setLoading(true);
      let response: string | undefined;
      if (user?.express) {
        if (!user?.clientId) {
          showAlert(dispatch, 'error', 'Problema al descargar el archivo');
          return;
        }
        const data = {
          fileName: fileName,
          requestType,
        };
        response = await expressDownloadFilesFromRequest(
          dispatch,
          data,
          user.clientId,
        );
      } else {
        const data = {
          fileName: fileName,
          requestType,
        };
        response = await downloadFilesFromRequest(dispatch, data);
      }
      if (response) {
        const link = document.createElement('a');
        link.href = response;
        link.download = fileName ?? 'download';
        document.body.appendChild(link);
        link.click();
        showAlert(dispatch, 'success', 'Archivo descargado correctamente');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadFiles = async () => {
    if (!canUpload) return;
    setMiniLoading(true);
    try {
      const formData = new FormData();
      if (!file) {
        showAlert(dispatch, 'error', 'No se ha seleccionado ningún archivo');
        return;
      }
      const newName = `${name}.${file.name.split('.').pop()}`;
      const fileToUpload = new File([file], newName, { type: file.type });
      formData.append('files', fileToUpload);
      if (user?.express) {
        if (!user?.clientId) {
          throw new Error('No se ha encontrado el clientId');
        }
        await expressUploadFilesFromRequest(
          dispatch,
          formData,
          { requestId: undefined, requestType },
          user?.clientId,
        );
      } else {
        await uploadFilesFromRequest(dispatch, formData, {
          requestId: undefined,
          requestType,
        });
      }
      await setUp();
      setFile(null);
      setUploadedFileName('');
    } catch (error) {
      console.error(error);
    }
    setMiniLoading(false);
  };

  useEffect(() => {
    handleClearFile();
  }, [name]);

  return (
    <TableRow>
      <TableCell style={firstCellStyle}>{name}</TableCell>
      <TableCell>
        {updatedAt ? formatDateToLocal(updatedAt, false) : 'No hay datos'}
      </TableCell>
      <TableCell style={{ textAlign: 'right' }}>
        {miniLoading ? (
          <CircularProgress />
        ) : (
          <div>
            {!status ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'right',
                }}
              >
                <UploaderInLine
                  handleFileChange={handleFileChange}
                  uploadedFileName={uploadedFileName}
                  handleSave={uploadFiles}
                  handleClearFile={handleClearFile}
                  disabled={!canUpload}
                />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'right',
                }}
              >
                {fileName && file === null && (
                  <IconButton
                    onClick={() => void handleDownload()}
                    disabled={loading || !canDownload}
                  >
                    <Download color={canDownload ? 'primary' : 'disabled'} />
                  </IconButton>
                )}
                <UploaderInLine
                  handleFileChange={handleFileChange}
                  uploadedFileName={uploadedFileName}
                  handleSave={uploadFiles}
                  handleClearFile={handleClearFile}
                  disabled={!canUpload}
                />
              </div>
            )}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

const UploadList = ({
  products,
  requestType,
  canDownload,
  canUpload,
}: UploadListProps) => {
  const [loading, setLoading] = useState(false);
  const [requirements, setRequirements] = useState([]);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const classes = useStyles();

  const dispatch = useDispatch();

  if (canDownload === undefined) canDownload = true;
  if (canUpload === undefined) canUpload = true;

  const getRequirementsDone = async () => {
    setLoading(true);
    try {
      if (user?.express) {
        if (!user?.clientId) throw new Error('No se ha encontrado el clientId');
        const response = await expressGetRequiredFiles(
          dispatch,
          requestType,
          undefined,
          products,
          user?.clientId,
        );
        setRequirements(response);
      } else {
        const response = await getRequiredFiles(
          dispatch,
          requestType,
          undefined,
          products,
        );
        setRequirements(response);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getRequirementsDone();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <div style={{ paddingTop: 20 }}>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead style={{ background: '#efefef' }}>
              <TableRow>
                <TableCell style={firstCellStyle}>Documento</TableCell>
                <TableCell>Subido el</TableCell>
                <TableCell style={{ textAlign: 'right' }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {requirements.map((requirement: Requirement, index: number) => (
                <RequirementItem
                  key={index}
                  name={requirement.name}
                  status={requirement.status}
                  fileName={requirement.file}
                  updatedAt={requirement.updatedAt}
                  setUp={getRequirementsDone}
                  requestType={requestType}
                  canDownload={canDownload}
                  canUpload={canUpload}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default UploadList;
